
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFormGrid,
  DialogFormSection,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import WeekOffPicker, { WEEKDAYS } from './common/WeekOffPicker';
import { attendanceRulesAPI, workspaceAdminAPI } from '../services/api';
import { toast } from 'sonner';
import { Plus, Clock, AlertTriangle, Briefcase, Coffee, Settings, Eye, Pencil, Trash2, CalendarClock, ShieldAlert } from 'lucide-react';
import { EmptyState, CardSkeleton } from './common/States';
import { StatCard } from './common/StatCard';
import { getErrorMessage } from '../lib/utils';

const emptyShiftForm = {
  name: '',
  start_time: '09:00:00',
  end_time: '18:00:00',
  grace_period_minutes: 0,
  early_exit_tolerance_minutes: 0,
  half_day_min_minutes: 240,
  full_day_min_minutes: 480,
  break_duration_minutes: 60,
  is_default: false,
  auto_half_day_enabled: true,
  fractional_leave_allowed: true,
  minimum_leave_unit: 0.5,
};

const normalizeShiftTime = (value, fallback) => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  if (/^\d{2}:\d{2}$/.test(raw)) return `${raw}:00`;
  const match = raw.match(/^(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : fallback;
};

const toShiftForm = (shift) => ({
  name: String(shift?.name || ''),
  start_time: normalizeShiftTime(shift?.start_time, '09:00:00'),
  end_time: normalizeShiftTime(shift?.end_time, '18:00:00'),
  grace_period_minutes: Number(shift?.grace_period_minutes ?? 0),
  early_exit_tolerance_minutes: Number(shift?.early_exit_tolerance_minutes ?? 0),
  half_day_min_minutes: Number(shift?.half_day_min_minutes ?? 240),
  full_day_min_minutes: Number(shift?.full_day_min_minutes ?? 480),
  break_duration_minutes: Number(shift?.break_duration_minutes ?? 60),
  is_default: Boolean(shift?.is_default),
  auto_half_day_enabled: Boolean(shift?.auto_half_day_enabled ?? true),
  fractional_leave_allowed: Boolean(shift?.fractional_leave_allowed ?? true),
  minimum_leave_unit: Number(shift?.minimum_leave_unit ?? 0.5),
  // Empty means this shift keeps the workspace week off.
  week_off_days: Array.isArray(shift?.week_off_days) ? shift.week_off_days : [],
});

const parseInteger = (value, fallback = 0) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseFloatValue = (value, fallback = 0.5) => {
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const AttendanceRuleConfig = ({
  currentTab = 'shifts',
  hiddenTabs = [],
  importShiftPath = '/admin/import/work_shift',
  shiftCreatePath = '/admin/rules-shifts/create',
  importRulePath = '/admin/import/attendance_rule',
  scope = 'tenant',
}) => {
  const navigate = useNavigate();
  // Global scope edits the shift template new workspaces are seeded with.
  // Attendance rules have no such template, so they are not fetched here —
  // the tab is hidden in that scope and the tenant endpoint would refuse.
  const isGlobalScope = scope === 'global';
  const [activeTab, setActiveTab] = useState(currentTab);
  const [shifts, setShifts] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedShift, setSelectedShift] = useState(null);
  const [isShiftViewOpen, setIsShiftViewOpen] = useState(false);
  const [isShiftEditOpen, setIsShiftEditOpen] = useState(false);
  const [shiftForm, setShiftForm] = useState(emptyShiftForm);
  const [savingShift, setSavingShift] = useState(false);
  const [deletingShiftId, setDeletingShiftId] = useState(null);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);

  const [newRule, setNewRule] = useState({
    name: '',
    rule_type: 'late_coming',
    grace_minutes: 0,
    threshold_count: 3,
    penalty_type: 'deduct_leave',
    penalty_value: 0.5,
    penalty_leave_type: 'casual',
    enable_short_leave_clubbing: false,
    reset_period: 'monthly',
    is_active: true
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalScope]);

  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  const isTabVisible = (tabId) => !hiddenTabs.includes(tabId);

  const fetchData = async () => {
    try {
      const [shiftRes, ruleRes] = await Promise.all([
        isGlobalScope ? workspaceAdminAPI.listGlobalShifts() : attendanceRulesAPI.getShifts(),
        isGlobalScope ? Promise.resolve({ data: [] }) : attendanceRulesAPI.getRules(),
      ]);
      setShifts(shiftRes.data);
      setRules(ruleRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load configuration'));
    } finally {
      setLoading(false);
    }
  };

  const openShiftView = (shift) => {
    setSelectedShift(shift);
    setIsShiftViewOpen(true);
  };

  const openShiftEdit = (shift) => {
    setSelectedShift(shift);
    setShiftForm(toShiftForm(shift));
    setIsShiftEditOpen(true);
  };

  const handleUpdateShift = async () => {
    if (!selectedShift?.id) return;
    if (!shiftForm.name.trim()) {
      toast.error('Shift name is required');
      return;
    }

    try {
      setSavingShift(true);
      const saveShift = isGlobalScope
        ? workspaceAdminAPI.updateGlobalShift
        : attendanceRulesAPI.updateShift;
      await saveShift(selectedShift.id, {
        ...shiftForm,
        name: shiftForm.name.trim(),
        start_time: normalizeShiftTime(shiftForm.start_time, '09:00:00'),
        end_time: normalizeShiftTime(shiftForm.end_time, '18:00:00'),
        grace_period_minutes: parseInteger(shiftForm.grace_period_minutes, 0),
        early_exit_tolerance_minutes: parseInteger(shiftForm.early_exit_tolerance_minutes, 0),
        half_day_min_minutes: parseInteger(shiftForm.half_day_min_minutes, 240),
        full_day_min_minutes: parseInteger(shiftForm.full_day_min_minutes, 480),
        break_duration_minutes: parseInteger(shiftForm.break_duration_minutes, 60),
        minimum_leave_unit: parseFloatValue(shiftForm.minimum_leave_unit, 0.5),
      });
      toast.success('Shift updated successfully');
      setIsShiftEditOpen(false);
      setSelectedShift(null);
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update shift'));
    } finally {
      setSavingShift(false);
    }
  };

  const handleDeleteShift = async (shift) => {
    if (!shift?.id) return;
    const confirmed = window.confirm(`Delete shift "${shift.name}"?`);
    if (!confirmed) return;

    try {
      setDeletingShiftId(shift.id);
      if (isGlobalScope) {
        await workspaceAdminAPI.deleteGlobalShift(shift.id);
      } else {
        await attendanceRulesAPI.deleteShift(shift.id);
      }
      toast.success('Shift deleted successfully');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete shift'));
    } finally {
      setDeletingShiftId(null);
    }
  };

  const handleCreateRule = async () => {
    try {
      await attendanceRulesAPI.createRule(newRule);
      toast.success("Rule created successfully");
      setIsRuleModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create rule'));
    }
  };

  const updateRuleField = async (rule, field, value) => {
    try {
      const payload = {
        ...rule,
        [field]: value,
      };
      await attendanceRulesAPI.updateRule(rule.id, payload);
      setRules((prev) =>
        prev.map((item) => (item.id === rule.id ? { ...item, [field]: value } : item))
      );
      toast.success("Rule updated successfully");
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update rule'));
    }
  };

  const getRuleIcon = (type) => {
    switch (type) {
      case 'work_hours': return <Clock className="h-4 w-4 text-primary" />;
      case 'grace_period': return <AlertTriangle className="h-4 w-4 text-[hsl(var(--chart-4))]" />;
      case 'overtime': return <Briefcase className="h-4 w-4 text-[hsl(var(--chart-3))]" />;
      case 'break_time': return <Coffee className="h-4 w-4 text-[hsl(var(--chart-4))]" />;
      default: return <Settings className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const renderShiftDetails = (shift) => (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Shift Name</p>
        <p className="font-medium">{shift.name}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Timings</p>
        <p className="font-medium">{shift.start_time} - {shift.end_time}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Week Off</p>
        <p>
          {Array.isArray(shift.week_off_days) && shift.week_off_days.length > 0
            ? shift.week_off_days
                .map((d) => WEEKDAYS.find((w) => w.value === d)?.label)
                .filter(Boolean)
                .join(', ')
            : 'Follows the workspace week off'}
        </p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Grace Period</p>
        <p>{shift.grace_period_minutes} mins</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Early Exit Tolerance</p>
        <p>{shift.early_exit_tolerance_minutes} mins</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Half Day Threshold</p>
        <p>{shift.half_day_min_minutes} mins</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Full Day Threshold</p>
        <p>{shift.full_day_min_minutes} mins</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Break Duration</p>
        <p>{shift.break_duration_minutes} mins</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Minimum Leave Unit</p>
        <p>{shift.minimum_leave_unit}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Default</p>
        <p>{shift.is_default ? 'Yes' : 'No'}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Auto Half Day</p>
        <p>{shift.auto_half_day_enabled ? 'Enabled' : 'Disabled'}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Fractional Leave</p>
        <p>{shift.fractional_leave_allowed ? 'Allowed' : 'Not Allowed'}</p>
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Created At</p>
        <p>{shift.created_at ? new Date(shift.created_at).toLocaleString() : '-'}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Attendance Rules</h2>
          <p className="text-muted-foreground">Configure shifts, working hours, and attendance policies</p>
        </div>
      </div>

      <Tabs defaultValue="shifts" className="space-y-4" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="sticky top-2 z-20 border border-border bg-background">
          {isTabVisible('shifts') && (
            <TabsTrigger value="shifts" className="gap-1.5"><Clock className="h-3.5 w-3.5" />Shifts & Schedules</TabsTrigger>
          )}
          {isTabVisible('rules') && (
            <TabsTrigger value="rules" className="gap-1.5"><Settings className="h-3.5 w-3.5" />Attendance Rules</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="shifts" className="space-y-4 animate-slide-up">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate(importShiftPath)}>
              Import Shifts
            </Button>
            <Button className="hover-lift" onClick={() => navigate(shiftCreatePath)}>
              <Plus className="mr-2 h-4 w-4" /> Add Shift
            </Button>
          </div>

          {!loading && shifts.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard label="Total Shifts" value={shifts.length} icon={Clock} tone="primary" />
              <StatCard
                label="Default Shifts"
                value={shifts.filter((shift) => shift.is_default).length}
                icon={CalendarClock}
                tone="info"
              />
            </div>
          ) : null}

          <Card className="border-border bg-card shadow-sm">
            <CardHeader>
              <CardTitle className="text-foreground">Shift List</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <CardSkeleton rows={5} />
              ) : shifts.length === 0 ? (
                <EmptyState
                  icon={Clock}
                  title="No shifts found"
                  description="Add work shifts to define timings, grace periods, and attendance thresholds."
                  actionLabel="Add Shift"
                  onAction={() => navigate(shiftCreatePath)}
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Timings</TableHead>
                      <TableHead>Grace</TableHead>
                      <TableHead>Early Exit</TableHead>
                      <TableHead>Break</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead className="w-[180px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {shifts.map((shift) => (
                      <TableRow key={shift.id}>
                        <TableCell className="font-medium">{shift.name}</TableCell>
                        <TableCell>{shift.start_time} - {shift.end_time}</TableCell>
                        <TableCell>{shift.grace_period_minutes} mins</TableCell>
                        <TableCell>{shift.early_exit_tolerance_minutes} mins</TableCell>
                        <TableCell>{shift.break_duration_minutes} mins</TableCell>
                        <TableCell>
                          {shift.is_default ? <Badge>Default</Badge> : <Badge variant="outline">No</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => openShiftView(shift)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openShiftEdit(shift)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={deletingShiftId === shift.id}
                              onClick={() => handleDeleteShift(shift)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="space-y-4 animate-slide-up">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate(importRulePath)}>
              Import Rules
            </Button>
            <Dialog open={isRuleModalOpen} onOpenChange={setIsRuleModalOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> Add Rule</Button>
              </DialogTrigger>
              <DialogContent size="md">
                <DialogHeader>
                  <DialogTitle>Create Attendance Rule</DialogTitle>
                </DialogHeader>
                <DialogFormSection className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Rule Name</Label>
                    <Input value={newRule.name} onChange={e => setNewRule({...newRule, name: e.target.value})} placeholder="Late Policy" />
                  </div>
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Rule Type</Label>
                      <Select value={newRule.rule_type} onValueChange={val => setNewRule({...newRule, rule_type: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="late_coming">Late Coming</SelectItem>
                          <SelectItem value="early_exit">Early Exit</SelectItem>
                          <SelectItem value="short_leave">Short Leave</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Threshold Count</Label>
                      <Input type="number" value={newRule.threshold_count} onChange={e => setNewRule({...newRule, threshold_count: parseInt(e.target.value)})} />
                    </div>
                    {newRule.rule_type === 'short_leave' && (
                        <div className="flex items-center space-x-2 pt-8">
                            <input type="checkbox" checked={newRule.enable_short_leave_clubbing} onChange={e => setNewRule({...newRule, enable_short_leave_clubbing: e.target.checked})} className="h-4 w-4" />
                            <Label>Allow Clubbing</Label>
                        </div>
                    )}
                  </DialogFormGrid>
                  <DialogFormGrid>
                    <div className="space-y-2">
                      <Label>Penalty Type</Label>
                      <Select value={newRule.penalty_type} onValueChange={val => setNewRule({...newRule, penalty_type: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="deduct_leave">Deduct Leave</SelectItem>
                          <SelectItem value="deduct_salary">Deduct Salary</SelectItem>
                          <SelectItem value="convert_to_short_leave">Convert to Short Leave</SelectItem>
                          <SelectItem value="convert_to_half_day">Convert to Half Day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Penalty Value</Label>
                      <Input type="number" step="0.5" value={newRule.penalty_value} onChange={e => setNewRule({...newRule, penalty_value: parseFloat(e.target.value)})} />
                    </div>
                  </DialogFormGrid>
                  {newRule.penalty_type === 'deduct_leave' && (
                    <div className="space-y-2">
                      <Label>Deduct From</Label>
                      <Select value={newRule.penalty_leave_type} onValueChange={val => setNewRule({...newRule, penalty_leave_type: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="casual">Casual Leave</SelectItem>
                          <SelectItem value="sick">Sick Leave</SelectItem>
                          <SelectItem value="annual">Annual Leave</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button onClick={handleCreateRule}>Create Rule</Button>
                </DialogFormSection>
              </DialogContent>
            </Dialog>
          </div>

          {!loading && rules.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <StatCard label="Total Rules" value={rules.length} icon={ShieldAlert} tone="primary" />
              <StatCard
                label="Active Rules"
                value={rules.filter((rule) => rule.is_active).length}
                icon={Settings}
                tone="success"
              />
            </div>
          ) : null}

          {loading ? (
            <CardSkeleton rows={5} />
          ) : rules.length === 0 ? (
            <Card className="border-border bg-card shadow-sm">
              <CardContent>
                <EmptyState
                  icon={ShieldAlert}
                  title="No attendance rules yet"
                  description="Create attendance rules to enforce grace periods, thresholds, and penalties."
                  actionLabel="Add Rule"
                  onAction={() => setIsRuleModalOpen(true)}
                />
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rules.map((rule, index) => (
                <Card key={rule.id} className="card-interactive hover-lift border-border bg-card shadow-sm transition-all duration-200" style={{ animationDelay: `${index * 0.05}s` }}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-muted/30 border-b border-border">
                    <CardTitle className="text-sm font-medium text-foreground">
                      {rule.name}
                    </CardTitle>
                    <AlertTriangle className="h-4 w-4 text-danger" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <Label className="text-sm text-muted-foreground">Grace Period (mins)</Label>
                        <Input
                          type="number"
                          value={rule.grace_minutes ?? 0}
                          onChange={(e) => updateRuleField(rule, 'grace_minutes', parseInt(e.target.value, 10) || 0)}
                          className="w-20 h-8"
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <Label className="text-sm text-muted-foreground">Late Threshold (mins)</Label>
                        <Input
                          type="number"
                          value={rule.threshold_count ?? 0}
                          onChange={(e) => updateRuleField(rule, 'threshold_count', parseInt(e.target.value, 10) || 0)}
                          className="w-20 h-8"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={isShiftViewOpen} onOpenChange={setIsShiftViewOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>View Shift</DialogTitle>
            <DialogDescription>Shift details in tabular workflow.</DialogDescription>
          </DialogHeader>
          {selectedShift ? renderShiftDetails(selectedShift) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={isShiftEditOpen} onOpenChange={setIsShiftEditOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit Shift</DialogTitle>
            <DialogDescription>Update timings, tolerances, and threshold settings.</DialogDescription>
          </DialogHeader>
          <DialogFormSection className="space-y-4 py-4">
            <DialogFormGrid>
              <div className="space-y-2">
                <Label>Shift Name</Label>
                <Input
                  value={shiftForm.name}
                  onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
                  placeholder="General Shift"
                />
              </div>
              <div className="flex items-center space-x-2 pt-8">
                <input
                  type="checkbox"
                  checked={shiftForm.is_default}
                  onChange={(e) => setShiftForm({ ...shiftForm, is_default: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label>Is Default Shift</Label>
              </div>
            </DialogFormGrid>
            <DialogFormGrid>
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  step="1"
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  step="1"
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                />
              </div>
            </DialogFormGrid>
            <div className="space-y-2">
              <Label>Week Off</Label>
              <WeekOffPicker
                value={shiftForm.week_off_days}
                onChange={(days) => setShiftForm({ ...shiftForm, week_off_days: days })}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to follow the workspace week off. Payroll divides pay by the
                days this shift works, so a six-day shift costs a loss-of-pay day less
                than a five-day one.
              </p>
            </div>
            <DialogFormGrid>
              <div className="space-y-2">
                <Label>Grace Period (mins)</Label>
                <Input
                  type="number"
                  value={shiftForm.grace_period_minutes}
                  onChange={(e) => setShiftForm({ ...shiftForm, grace_period_minutes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Early Exit Tolerance (mins)</Label>
                <Input
                  type="number"
                  value={shiftForm.early_exit_tolerance_minutes}
                  onChange={(e) => setShiftForm({ ...shiftForm, early_exit_tolerance_minutes: e.target.value })}
                />
              </div>
            </DialogFormGrid>
            <DialogFormGrid>
              <div className="space-y-2">
                <Label>Half Day Min (mins)</Label>
                <Input
                  type="number"
                  value={shiftForm.half_day_min_minutes}
                  onChange={(e) => setShiftForm({ ...shiftForm, half_day_min_minutes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Full Day Min (mins)</Label>
                <Input
                  type="number"
                  value={shiftForm.full_day_min_minutes}
                  onChange={(e) => setShiftForm({ ...shiftForm, full_day_min_minutes: e.target.value })}
                />
              </div>
            </DialogFormGrid>
            <DialogFormGrid>
              <div className="space-y-2">
                <Label>Break Duration (mins)</Label>
                <Input
                  type="number"
                  value={shiftForm.break_duration_minutes}
                  onChange={(e) => setShiftForm({ ...shiftForm, break_duration_minutes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Minimum Leave Unit</Label>
                <Input
                  type="number"
                  step="0.25"
                  value={shiftForm.minimum_leave_unit}
                  onChange={(e) => setShiftForm({ ...shiftForm, minimum_leave_unit: e.target.value })}
                />
              </div>
            </DialogFormGrid>
            <DialogFormGrid>
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  checked={shiftForm.auto_half_day_enabled}
                  onChange={(e) => setShiftForm({ ...shiftForm, auto_half_day_enabled: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label>Auto Half Day Enabled</Label>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  checked={shiftForm.fractional_leave_allowed}
                  onChange={(e) => setShiftForm({ ...shiftForm, fractional_leave_allowed: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label>Fractional Leave Allowed</Label>
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsShiftEditOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateShift} disabled={savingShift}>
              {savingShift ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceRuleConfig;
